// InterruptionNotice — the ONE notice for an interrupted turn (D18, FR-2.5):
// what happened, what is kept, and the one thing you can do. Props-driven and
// pure: the copy comes from `interruptionCopy`; the action button renders only
// when the caller wires `onAction` (no dead button), so a notice resting in
// scrollback reads as history while the live one offers its recovery.
// alarm-discipline: it never steals focus; colour follows consequence and is
// always paired with an icon + words.

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Button } from "../../Button/Button.js";
import { Icon } from "../../Icon/Icon.js";
import { Text } from "../../Text/Text.js";
import {
    type InterruptionTone,
    interruptionCopy,
} from "../ChatMessage/interruptionCopy.js";
import type { Interruption, InterruptionAction } from "../types.js";
import "./InterruptionNotice.css";

const iconOf = (tone: InterruptionTone) =>
    tone === "neutral"
        ? "stopped"
        : tone === "attention"
          ? "warning"
          : "danger";

export const InterruptionNotice = ({
    interruption,
    onAction,
}: {
    readonly interruption: Interruption;
    readonly onAction?: (action: InterruptionAction) => void;
}): ReactNode => {
    const { i18n } = useLingui();
    const copy = interruptionCopy(interruption);
    const action = copy.action;
    return (
        <div
            className={`bilda-interruption bilda-interruption--${copy.tone}`}
            role="status"
        >
            <div className="bilda-interruption__head">
                <Icon name={iconOf(copy.tone)} />
                <Text as="span" size="sm" weight="medium">
                    {i18n._(copy.happened)}
                </Text>
            </div>
            {copy.kept.map((line) => (
                <Text key={line.id} as="p" size="sm" tone="dim">
                    {i18n._(line)}
                </Text>
            ))}
            {action !== undefined && onAction !== undefined ? (
                <div>
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onAction(action.type)}
                    >
                        {i18n._(action.label)}
                    </Button>
                </div>
            ) : null}
        </div>
    );
};
